import React, { useState, useEffect } from 'react';
import { useTranslations } from 'use-intl';
import { Typography } from '@/UIlibrary/Typography/Typography';
import InputField from '@/UIlibrary/InputField/InputField';
import { toSnakeCase } from './utils';
import { useChatStore } from '../useChat';
import ConfirmModal from '@/stories/Components/ConfirmModal/ConfirmModal';
import { useSupabaseStore } from '../supabaseStore';
import TextArea from '@/UIlibrary/TextArea/TextArea';
import { useOrganizationId } from '../useOrganizationId';
interface CreateTemplateMessageModalProps {
	isOpen: boolean;
	onClose: () => void;
	defaultPhone?: string;
}

const CreateTemplateModal: React.FC<CreateTemplateMessageModalProps> = ({ isOpen, onClose }) => {
	const t = useTranslations('Support');
	const supabase = useSupabaseStore((state) => state.supabase);
	const { selectedChat } = useChatStore();
	const { organizationId } = useOrganizationId(false);
	const [chatId, setChatId] = useState<string>();
	const [formData, setFormData] = useState({
		templateName: '',
		templateContent: ''
	});
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState('');

	useEffect(() => {
		setChatId(selectedChat?.id || '');
	}, [selectedChat]);

	const handleCreateTemplate = async () => {
		if (!supabase) return;
		if (!formData.templateName.trim() || !formData.templateContent.trim()) {
			setError(t('TemplateNameAndContentRequired'));
			return;
		}

		if (!/^[a-zA-Z_ ]+$/.test(formData.templateName)) {
			setError(t('TemplateNameInvalid'));
			return;
		}
		setIsLoading(true);
		setError('');
		try {
			const formattedName = 'temp_' + toSnakeCase(formData.templateName);
			const response = await supabase.functions.invoke('whatsapp-create-template', {
				body: {
					name: formattedName,
					content: formData.templateContent,
					language: 'es_MX',
					chatId,
					whatsapp_org_id: organizationId
				}
			});

			if (response.error) throw new Error(response.error);

			handleClose();
		} catch (err) {
			console.error('Error creating template:', err);
			setError(t('ErrorCreatingTemplate'));
		} finally {
			setIsLoading(false);
		}
	};

	const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { name, value } = event.target;
		setFormData((prevData) => ({
			...prevData,
			[name]: value
		}));
	};

	const handleClose = () => {
		onClose();
		setError('');
		setFormData({
			templateName: '',
			templateContent: ''
		});
	};

	const templateFormContent = (
		<div className="flex flex-col gap-4">
			<div className="my-2" />

			<InputField
				label={t('TemplateName')}
				placeholder={t('TemplateNamePlaceholder')}
				type="text"
				name="templateName"
				onChange={handleChange}
				value={formData.templateName}
				error={!!error}
				hint={error || t('TemplateNameHint')}
			/>
			<TextArea
				label={t('TemplateContent')}
				placeholder={t('TemplateContentPlaceholder')}
				name="templateContent"
				onChange={handleChange}
				value={formData.templateContent}
				rows={4}
				error={!!error}
				hint={error !== t('TemplateNameInvalid') ? error : ''}
			/>
			<Typography color="subtitle" size="sm">
				{t('CreateTemplateMessageDisclaimer')}
			</Typography>
		</div>
	);

	return (
		<ConfirmModal
			isOpen={isOpen}
			onClose={handleClose}
			title={t('CustomTemplate')}
			subtitle={t('CreateTemplateMessageDescription')}
			confirmActionText={isLoading ? t('Creating') : t('CreateTemplate')}
			onConfirm={handleCreateTemplate}
			onCancel={handleClose}
			confirmBtnLoading={isLoading}
			confirmBtnTestId="sendTemplateBtn"
			cancelBtnTestId="cancelTemplateBtn"
			type="confirm"
			maxWidth={650}
		>
			{templateFormContent}
		</ConfirmModal>
	);
};

export default CreateTemplateModal;
